import { Controller } from "@hotwired/stimulus"
import RequestHelper from "../helpers/request_helper"

export default class extends Controller {
  static get targets() {
    return [
      "tab", "search", "giftLevel", "giftLevelLabel", "filterEnabled", "filterValue",
      "filtersLabel", "showAllLabel", "showSelectedButton", "resultsContainer", "results", "message"
    ];
  }

  static get values() {
    return { productId: Number, assignedGalleryIds: Array };
  }

  static get classes() {
    return ["active", "showSelectedActive", "oddCol", "evenCol"];
  }

  connect() {
    this.activeTab = "global";
    this.showSelectedOnly = false;
    this.showAllSubGalleries = false;
    this.selectedGalleryIds = new Set(this.assignedGalleryIdsValue);

    // R3: kept as a manual listener rather than data-action. formElement is the ambient
    // product-edit <form> reached via closest() -- a container this controller does not own
    // (rendered by the parent view, shared with unrelated fields outside this cluster).
    // handleResultsChange, by contrast, is wired via data-action on resultsContainerTarget in the
    // markup (Pattern 23): the container is this controller's own target, so a single data-action
    // there catches bubbled "change" events the same way the old addEventListener did, including
    // from checkboxes fetchGalleries() adds later via innerHTML.
    this.handleSubmit = this.handleSubmit.bind(this);

    this.formElement = this.element.closest("form");
    this.formElement.addEventListener("submit", this.handleSubmit);

    this.fetchGalleries();
  }

  disconnect() {
    this.formElement.removeEventListener("submit", this.handleSubmit);
  }

  handleSubmit() {
    this.snapshotCurrentSelections();
    this.resultsContainerTarget.querySelectorAll(".gallery-id-preserve").forEach(el => el.remove());

    const domIds = new Set(
      Array.from(this.resultsContainerTarget.querySelectorAll('input[name="product[gallery_ids][]"]')).
        map(el => parseInt(el.value, 10))
    );

    this.selectedGalleryIds.forEach(id => {
      if (domIds.has(id)) { return; }

      const hidden = document.createElement("input");
      hidden.type = "hidden";
      hidden.name = "product[gallery_ids][]";
      hidden.value = id;
      hidden.className = "gallery-id-preserve";
      this.resultsContainerTarget.appendChild(hidden);
    });
  }

  handleResultsChange(event) {
    // R1: target.dataset.id is per-checkbox (each rendered gallery has its own id, per
    // _gallery_checkboxes.html.haml's `data: { id: parent.id }` inside a loop) -- a descendant's
    // own configuration, not a controller-root constant, so it can't become a Stimulus Value.
    const target = event.target;

    if (target.matches(".parent_gallery_checkbox")) {
      const id = target.dataset.id;
      const checked = target.checked;
      // A plain property assignment, not .click(): nothing listens for change on an individual
      // sub-gallery checkbox, so there is no handler that would need firing.
      this.resultsContainerTarget.querySelectorAll(`.sub_gallery_to_${id}`).forEach(cb => { cb.checked = checked; });
    } else if (target.matches(".select-all-checkbox")) {
      const checked = target.checked;
      target.closest(".gift-level-section").querySelectorAll('input[type="checkbox"]').forEach(cb => { cb.checked = checked; });
    }
  }

  setTab(event) {
    event.preventDefault();
    const li = event.currentTarget.closest("li");
    const activeClass = this.hasActiveClass ? this.activeClass : "active";
    this.tabTargets.forEach(t => t.classList.remove(activeClass));
    li.classList.add(activeClass);
    // R1: li.dataset.tab differs per tab <li> ("global" vs "all") -- a target's own
    // configuration, not a single controller-wide constant.
    this.activeTab = li.dataset.tab;
    this.fetchGalleries();
  }

  stopPropagation(event) {
    event.stopPropagation();
  }

  giftLevelChanged() {
    this.updateGiftLevelLabel();
  }

  filterChanged(event) {
    // R1: dataset.filterKey varies per filter checkbox/select (gallery_filter_options.each in
    // _form.html.haml) -- per-instance, not a controller-root constant.
    const checkbox = event.target;
    const key = checkbox.dataset.filterKey;
    const select = this.filterValueTargets.find(s => s.dataset.filterKey === key);
    select.disabled = !checkbox.checked;
    this.updateFiltersLabel();
  }

  updateGiftLevelLabel() {
    const selected = this.giftLevelTargets.filter(cb => cb.checked);
    this.giftLevelLabelTarget.textContent = selected.length ?
      selected.map(cb => `$${Number(cb.value) / 100}`).join(", ") :
      "All Gift Levels";
  }

  updateFiltersLabel() {
    const count = this.filterEnabledTargets.filter(cb => cb.checked).length;
    this.filtersLabelTarget.textContent = count ? `Filters (${count})` : "Filters";
  }

  toggleShowSelected(event) {
    event.preventDefault();
    this.showSelectedOnly = !this.showSelectedOnly;
    const activeClass = this.hasShowSelectedActiveClass ? this.showSelectedActiveClass : "show-selected-active";
    this.showSelectedButtonTarget.classList.toggle(activeClass, this.showSelectedOnly);
    this.applySelectedFilter();
  }

  toggleShowAll(event) {
    event.preventDefault();
    this.showAllSubGalleries = !this.showAllSubGalleries;
    this.resultsContainerTarget.querySelectorAll("details").forEach(details => {
      if (this.showAllSubGalleries) {
        details.setAttribute("open", "");
      } else {
        details.removeAttribute("open");
      }
    });
    this.showAllLabelTarget.textContent = this.showAllSubGalleries ? "Collapse sub-galleries" : "Show all sub-galleries";
    const activeClass = this.hasShowSelectedActiveClass ? this.showSelectedActiveClass : "show-selected-active";
    event.currentTarget.classList.toggle(activeClass, this.showAllSubGalleries);
  }

  applySelectedFilter() {
    if (!this.showSelectedOnly) {
      this.resultsContainerTarget.
        querySelectorAll(".parent-gallery, .non-parent-non-sub-gallery, .col-sm-6, .select-all-label").
        forEach(el => { el.style.display = ""; });
      this.messageTarget.style.display = "none";
      this.reindexColumnPadding();
      return;
    }

    this.resultsContainerTarget.querySelectorAll(".select-all-label").forEach(el => { el.style.display = "none"; });

    let anyVisible = false;

    this.resultsContainerTarget.querySelectorAll(".parent-gallery").forEach(el => {
      // Checked here includes any nested sub-gallery, not just the parent's own checkbox
      const isChecked = !!el.querySelector('input[type="checkbox"]:checked');
      el.style.display = isChecked ? "" : "none";
      if (isChecked) { anyVisible = true; }
    });

    this.resultsContainerTarget.querySelectorAll(".non-parent-non-sub-gallery").forEach(el => {
      const isChecked = !!el.querySelector('input[type="checkbox"]:checked');
      el.style.display = isChecked ? "" : "none";
      if (isChecked) { anyVisible = true; }
    });

    this.resultsContainerTarget.querySelectorAll(".col-sm-6").forEach(el => {
      const hasVisible = Array.from(el.querySelectorAll(".parent-gallery, .non-parent-non-sub-gallery")).
        some(row => row.style.display !== "none");
      el.style.display = hasVisible ? "" : "none";
    });

    this.messageTarget.style.display = anyVisible ? "none" : "";
    this.reindexColumnPadding();
  }

  snapshotCurrentSelections() {
    this.resultsContainerTarget.querySelectorAll('input[name="product[gallery_ids][]"]').forEach(el => {
      const id = parseInt(el.value, 10);
      if (el.checked) {
        this.selectedGalleryIds.add(id);
      } else {
        this.selectedGalleryIds.delete(id);
      }
    });
  }

  restoreSelections() {
    this.resultsContainerTarget.querySelectorAll('input[name="product[gallery_ids][]"]').forEach(el => {
      const id = parseInt(el.value, 10);
      if (this.selectedGalleryIds.has(id)) { el.checked = true; }
    });
  }

  reindexColumnPadding() {
    const sections = Array.from(this.resultsContainerTarget.querySelectorAll(".gift-level-section"));
    const oddClass = this.hasOddColClass ? this.oddColClass : "odd-col";
    const evenClass = this.hasEvenColClass ? this.evenColClass : "even-col";
    sections.forEach(section => section.classList.remove(oddClass, evenClass));
    sections.
      filter(section => section.offsetParent !== null).
      forEach((section, i) => { section.classList.add(i % 2 === 0 ? oddClass : evenClass); });
  }

  fetchGalleries() {
    this.snapshotCurrentSelections();

    const params = new URLSearchParams();
    params.set("q", this.searchTarget.value);
    if (this.hasProductIdValue) { params.set("product_id", this.productIdValue); }
    params.set("tab", this.activeTab);
    this.giftLevelTargets.filter(cb => cb.checked).forEach(cb => params.append("gift_level_cents[]", cb.value));
    this.filterEnabledTargets.filter(cb => cb.checked).forEach(cb => {
      const key = cb.dataset.filterKey;
      const select = this.filterValueTargets.find(s => s.dataset.filterKey === key);
      params.set(key, select.value);
    });

    // Rails' CSRF middleware (verify_same_origin_request) rejects any .js-format GET response
    // that doesn't look like an XHR, whether or not the action itself branches on the header.
    const options = RequestHelper.defaultRequestOptions();
    options.headers = Object.assign({}, options.headers, { "X-Requested-With": "XMLHttpRequest" });

    fetch(`/admin/galleries.js?${params.toString()}`, options).
      then(response => {
        if (!response.ok) { throw new Error(`Gallery search failed (${response.status})`); }
        return response.text();
      }).
      then(html => {
        // Server-rendered HTML from our own /admin/galleries.js endpoint (renders
        // admin/suppliers/products/_gallery_checkboxes, which auto-escapes every gallery name
        // through Rails' `=` output). Same-origin, not user input -- accepted per S1.
        this.resultsTarget.innerHTML = html;
        this.showSelectedOnly = false;
        const activeClass = this.hasShowSelectedActiveClass ? this.showSelectedActiveClass : "show-selected-active";
        this.showSelectedButtonTarget.classList.remove(activeClass);
        this.messageTarget.style.display = "none";
        this.restoreSelections();
        this.reindexColumnPadding();
      }).
      catch(this.logFailedCall);
  }

  search(event) {
    event.preventDefault();
    this.fetchGalleries();
  }

  handleKeydown(event) {
    if (event.keyCode === 13) { this.search(event); }
  }

  clearSearch(event) {
    event.preventDefault();
    this.searchTarget.value = "";
    this.giftLevelTargets.forEach(cb => { cb.checked = false; });
    this.filterEnabledTargets.forEach(cb => { cb.checked = false; });
    this.filterValueTargets.forEach(select => {
      select.value = "true";
      select.disabled = true;
    });
    this.updateGiftLevelLabel();
    this.updateFiltersLabel();
    this.search(event);
  }

  logFailedCall(response) {
    console.warn(response);
  }
}
